import { useCallback, useEffect, useState } from 'react'
import { FileError, useDropzone } from 'react-dropzone'
import styled, { css } from 'styled-components'
// assets
import CloudUpload from 'assets/images/icons/CloudUpload.svg'
// types and utils
import { Button, Alert } from '@outbound-ai/ursa'
import { kbToMb } from 'utils'
import useWindowDimensions from 'utils/Utils'

interface IProps {
  onFile: (file: File) => void
  fileStyle?: IStyle
}

interface IStyle {
  height?: string
}

const MAX_ALLOWED_FILE_SIZE_IN_MB = 51
const ALLOWED_FILE_TYPE = 'text/csv'

enum FileCodeEnum {
  fileToLarge = 'file-too-large',
  fileTypeUnsupported = 'file-type-unsupported',
  fileInvalidType = 'file-invalid-type',
}

export const FileDragDrop = ({ onFile, fileStyle }: IProps) => {
  const windowHeight = useWindowDimensions().windowHeight

  const [showAlert, setAlert] = useState(false)

  const [fileError, setFileError] = useState<FileError[]>()

  const onDrop = useCallback(
    (files: File[]) => {
      if (files.length) {
        onFile(files[0])
      }
    },
    [onFile],
  )

  const validator = useCallback((file: File) => {
    const errors = []
    if (kbToMb(file.size) > MAX_ALLOWED_FILE_SIZE_IN_MB)
      errors.push({
        code: FileCodeEnum.fileToLarge,
        message: `"${file.name}" is too large. File size must be less than 50MB.`,
      })
    if (file.type !== ALLOWED_FILE_TYPE)
      errors.push({
        code: FileCodeEnum.fileTypeUnsupported,
        message: `File "${file.name}" was rejected. File type is not supported.`,
      })

    return errors.length ? errors : null
  }, [])

  const dropzoneSettings = {
    onDrop,
    accept: ALLOWED_FILE_TYPE,
    maxFiles: 1,
    validator,
  }
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone(dropzoneSettings)

  useEffect(() => {
    if (fileRejections.length) {
      fileRejections.forEach(({ errors }) => {
        setFileError(errors)
        if (errors?.length > 0) {
          setAlert(true)
        }
      })
    }
  }, [fileRejections])

  return (
    <>
      {fileError &&
        fileError.map(({ code, message }, index) => {
          if (code === FileCodeEnum.fileTypeUnsupported || code === FileCodeEnum.fileToLarge) {
            return (
              <Alert
                variant="danger"
                key={index}
                show={showAlert}
                onClickClose={setAlert}
                dismissible={true}
                headerChildren={message}
              />
            )
          }
        })}

      <Div {...getRootProps()} isDragActive={isDragActive} windowHeight={windowHeight} fileStyle={fileStyle}>
        <input data-testid="file-upload-input" {...getInputProps()} />

        <div className="upload-icon">
          <img src={CloudUpload} />
        </div>

        <h2 data-testid="file-upload-message">Drop your .csv file here, or </h2>
        <Button radius="rounded" variant="info" size="small" className="browse-btn">
          Browse
        </Button>
        <p data-testid="file-upload-constraint">Max. File Size 50MB</p>
      </Div>
    </>
  )
}

const Div = styled.div<{ isDragActive: boolean; windowHeight: number; fileStyle: IStyle }>`
  width: 100%;
  height: ${({ fileStyle }) => (fileStyle?.height ? fileStyle.height : '17rem')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.grey.neutral};
  border: 1px dashed ${({ theme }) => theme.colors.grey.light};
  background: ${({ theme }) => theme.colors.neutral.lighter};
  border-radius: 0.5rem;
  z-index: 99;
  cursor: pointer;
  h2 {
    font-size: 1.125rem;
    color: ${({ theme }) => theme.colors.neutral.dark};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin-top: 0.75rem;
    span {
      color: ${({ theme }) => theme.colors.info.darker};
      text-decoration: underline;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
  }

  p {
    font-size: 0.875rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    text-align: center;
    color: ${({ theme }) => theme.colors.grey.neutral};
    margin-top: 1.5rem;
  }
  /* .browse-btn {
    width: 4.625rem;
    height: 2rem;
  } */
  ${({ isDragActive }) =>
    isDragActive &&
    css`
      background-color: ${({ theme }) => `${theme.colors.primary.light}55`};
    `}
`
